import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Loader2, KeyRound, RotateCcw, Check, Gift, Copy, Link as LinkIcon, BookOpen, BookLock, Pencil, Eye, Send } from "lucide-react";

export default function AdminCodes() {
  const [token] = useState(() => getAdminToken());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [resetting, setResetting] = useState(null);
  const [justReset, setJustReset] = useState(null);
  const [resending, setResending] = useState(null);
  const [justResent, setJustResent] = useState(null);
  // Complimentary access grant
  const [cName, setCName] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cNote, setCNote] = useState("");
  const [cCap, setCCap] = useState(5);
  const [offerBook, setOfferBook] = useState(true);
  const [offerVault, setOfferVault] = useState(true);
  const [granting, setGranting] = useState(false);
  const [grantErr, setGrantErr] = useState("");
  const [granted, setGranted] = useState(null);
  const [bookGranting, setBookGranting] = useState(false);
  const [bookResult, setBookResult] = useState(null);
  const [bookErr, setBookErr] = useState("");
  const [bookSentTo, setBookSentTo] = useState("");
  const [editingCode, setEditingCode] = useState(null);
  const [sentToDraft, setSentToDraft] = useState("");
  const [savingSentTo, setSavingSentTo] = useState(false);
  const [vaultGranting, setVaultGranting] = useState(false);
  const [vaultResult, setVaultResult] = useState(null);
  const [vaultErr, setVaultErr] = useState("");
  const [copied, setCopied] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/codes`, auth);
      setRows(data.codes || []);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not load access codes.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const resetDevices = async (code) => {
    if (!window.confirm(`Reset the device list for ${code}? The reader can then activate on a fresh device.`)) return;
    setResetting(code); setErr("");
    try {
      await axios.post(`${API}/admin/codes/${encodeURIComponent(code)}/reset-devices`, {}, auth);
      setRows((r) => r.map((x) => (x.access_code === code ? { ...x, device_count: 0 } : x)));
      setJustReset(code);
      setTimeout(() => setJustReset((c) => (c === code ? null : c)), 2500);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not reset that code. Please try again.");
    } finally { setResetting(null); }
  };

  const resendAccess = async (code) => {
    setResending(code); setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/codes/${encodeURIComponent(code)}/resend-access`, {}, auth);
      const r = data.result || {};
      setJustResent(code);
      setTimeout(() => setJustResent((c) => (c === code ? null : c)), 2800);
      if (r.email_status && !String(r.email_status).startsWith("sent")) {
        setErr(`Access message: email ${r.email_status}.`);
      }
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr(e.response?.data?.detail || "Could not resend the access message.");
    } finally { setResending(null); }
  };

  const filtered = rows.filter((r) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (r.access_code || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q) || (r.comp_name || "").toLowerCase().includes(q) || (r.sent_to || "").toLowerCase().includes(q);
  });

  const compLinkFor = (r) => {
    const tiers = r.tiers || [];
    if (tiers.includes("book_owner")) return `${window.location.origin}/?code=${encodeURIComponent(r.access_code)}`;
    if (tiers.includes("vault_member")) return `${window.location.origin}/vault?code=${encodeURIComponent(r.access_code)}`;
    return `${window.location.origin}/?code=${encodeURIComponent(r.access_code)}`;
  };

  const grant = async () => {
    const tiers = [];
    if (offerBook) tiers.push("book_owner");
    if (offerVault) tiers.push("vault_member");
    if (tiers.length === 0) { setGrantErr("Choose at least one offering (the book or the Language Vault)."); return; }
    setGranting(true); setGrantErr(""); setGranted(null);
    try {
      const { data } = await axios.post(`${API}/admin/codes/comp`, {
        name: cName.trim() || null,
        email: cEmail.trim().toLowerCase() || null,
        note: cNote.trim() || null,
        tiers,
        device_cap: Number(cCap) || 5,
        origin_url: window.location.origin,
      }, auth);
      setGranted(data);
      setCName(""); setCEmail(""); setCNote(""); setCCap(5);
      load();
      copy(compLinkFor(data), "complink");
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setGrantErr(e.response?.data?.detail || "Could not create the complimentary code.");
    } finally { setGranting(false); }
  };

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied((k) => (k === key ? "" : k)), 2000); } catch (e) { /* ignore */ }
  };

  const generateBookLink = async () => {
    setBookGranting(true); setBookErr(""); setBookResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/codes/comp`, {
        tiers: ["book_owner"],
        note: "Free book link (partnership / promo)",
        sent_to: bookSentTo.trim() || null,
        device_cap: 5,
        origin_url: window.location.origin,
      }, auth);
      setBookResult(data);
      setBookSentTo("");
      load();
      if (data.read_link) copy(data.read_link, "booklink");
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setBookErr(e.response?.data?.detail || "Could not create the free book link.");
    } finally { setBookGranting(false); }
  };

  const startEditSentTo = (r) => { setEditingCode(r.access_code); setSentToDraft(r.sent_to || ""); };

  const saveSentTo = async (code) => {
    setSavingSentTo(true);
    try {
      const value = sentToDraft.trim();
      await axios.patch(`${API}/admin/codes/${encodeURIComponent(code)}`, { sent_to: value || null }, auth);
      setRows((r) => r.map((x) => (x.access_code === code ? { ...x, sent_to: value || null } : x)));
      setEditingCode(null); setSentToDraft("");
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
    } finally { setSavingSentTo(false); }
  };

  const vaultLinkFor = (r) => `${window.location.origin}/vault?code=${encodeURIComponent(r.access_code)}`;

  const generateVaultLink = async () => {
    setVaultGranting(true); setVaultErr(""); setVaultResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/codes/comp`, {
        tiers: ["vault_member"],
        note: "Free Language Vault link (complimentary)",
        device_cap: 5,
        origin_url: window.location.origin,
      }, auth);
      setVaultResult(data);
      load();
      copy(vaultLinkFor(data), "vaultlink");
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setVaultErr(e.response?.data?.detail || "Could not create the free Vault link.");
    } finally { setVaultGranting(false); }
  };

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-codes">
      <div className="mx-auto max-w-5xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <h1 className="font-display text-3xl text-ink">Access Codes</h1>
        <p className="mt-1 text-sm text-ink-soft">
          Each full-book code may be used on up to its device cap. If a reader runs out of devices,
          reset the list here so they can activate again — no email needed.
        </p>

        {/* Abrupt one-click FREE BOOK link (partnerships / promo / development) */}
        <div className="mt-6 overflow-hidden rounded-2xl ring-1 ring-ink/10" data-testid="free-book-panel" style={{ background: "linear-gradient(180deg,#1b1d22,#2c2e33)" }}>
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-gold/20 text-gold"><BookOpen className="h-5 w-5" /></span>
              <div>
                <h2 className="font-display text-lg text-ivory-50">Free book link</h2>
                <p className="mt-0.5 max-w-md text-sm text-ivory-200/80">One tap creates a shareable link that unlocks <em>the book only</em> — free, no payment, no Language Vault. Perfect for partnerships, promo and development readers.</p>
              </div>
            </div>
            <button type="button" onClick={generateBookLink} disabled={bookGranting} data-testid="free-book-generate"
              className="inline-flex flex-none items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 font-display text-ink transition hover:brightness-105 disabled:opacity-60">
              {bookGranting ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />} Create free book link
            </button>
          </div>

          <div className="px-5 pb-5 sm:px-6">
            <label className="text-[11px] uppercase tracking-wide text-ivory-200/70">Sent to (optional — track who this link went to)</label>
            <input value={bookSentTo} onChange={(e) => setBookSentTo(e.target.value)} data-testid="free-book-sent-to"
              placeholder="e.g. Oprah's book club · @wellness_partner · Jane Doe"
              className="mt-1.5 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2.5 text-sm text-ivory-50 placeholder:text-ivory-200/40 focus:border-gold focus:outline-none sm:max-w-md" />
          </div>

          {bookErr && <p className="px-6 pb-5 text-sm text-terracotta-200" data-testid="free-book-error">{bookErr}</p>}

          {bookResult && (
            <div className="border-t border-white/10 bg-black/20 px-5 py-4 sm:px-6" data-testid="free-book-result">
              <p className="text-[11px] uppercase tracking-wide text-ivory-200/70">Copied to your clipboard — ready to send</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <code className="max-w-full truncate rounded-lg bg-black/40 px-3 py-2 text-sm text-gold" data-testid="free-book-link">{bookResult.read_link || bookResult.access_code}</code>
                <button onClick={() => copy(bookResult.read_link || bookResult.access_code, "booklink")} data-testid="free-book-copy"
                  className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-3 py-1.5 text-xs text-gold hover:bg-gold/10">
                  {copied === "booklink" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied === "booklink" ? "Copied" : "Copy link"}
                </button>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-ivory-200/80">Code: {bookResult.access_code}</span>
              </div>
              <p className="mt-2 text-xs text-ivory-200/60">Book only · up to {bookResult.device_cap} devices · the recipient opens the link and starts reading, no sign-up.</p>
              {bookResult.sent_to && <p className="mt-1 text-xs text-gold/80" data-testid="free-book-sent-to-result">Sent to: {bookResult.sent_to}</p>}
            </div>
          )}
        </div>

        {/* Abrupt one-click FREE LANGUAGE VAULT link */}
        <div className="mt-4 overflow-hidden rounded-2xl ring-1 ring-ink/10" data-testid="free-vault-panel" style={{ background: "linear-gradient(180deg,#14251f,#1d3a2f)" }}>
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-sage/25 text-sage"><BookLock className="h-5 w-5" /></span>
              <div>
                <h2 className="font-display text-lg text-ivory-50">Free Language Vault link</h2>
                <p className="mt-0.5 max-w-md text-sm text-ivory-200/80">One tap creates a shareable link that unlocks <em>the Language Vault only</em> — free, no payment. The recipient lands straight inside the Vault sanctuary.</p>
              </div>
            </div>
            <button type="button" onClick={generateVaultLink} disabled={vaultGranting} data-testid="free-vault-generate"
              className="inline-flex flex-none items-center justify-center gap-2 rounded-full bg-sage px-6 py-3 font-display text-ink transition hover:brightness-105 disabled:opacity-60">
              {vaultGranting ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />} Create free Vault link
            </button>
          </div>

          {vaultErr && <p className="px-6 pb-5 text-sm text-terracotta-200" data-testid="free-vault-error">{vaultErr}</p>}

          {vaultResult && (
            <div className="border-t border-white/10 bg-black/20 px-5 py-4 sm:px-6" data-testid="free-vault-result">
              <p className="text-[11px] uppercase tracking-wide text-ivory-200/70">Copied to your clipboard — ready to send</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <code className="max-w-full truncate rounded-lg bg-black/40 px-3 py-2 text-sm text-sage" data-testid="free-vault-link">{vaultLinkFor(vaultResult)}</code>
                <button onClick={() => copy(vaultLinkFor(vaultResult), "vaultlink")} data-testid="free-vault-copy"
                  className="inline-flex items-center gap-1.5 rounded-full border border-sage/40 px-3 py-1.5 text-xs text-sage hover:bg-sage/10">
                  {copied === "vaultlink" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied === "vaultlink" ? "Copied" : "Copy link"}
                </button>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-ivory-200/80">Code: {vaultResult.access_code}</span>
              </div>
              <p className="mt-2 text-xs text-ivory-200/60">Language Vault only · up to {vaultResult.device_cap} devices · does not include the book or the full art gallery.</p>
            </div>
          )}
        </div>

        {/* Complimentary / gifted full access */}
        <div className="mt-6 rounded-2xl bg-ivory-50 p-5 ring-1 ring-ivory-400/60 sm:p-6" data-testid="comp-access-panel">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-sage/25 text-ink-soft"><Gift className="h-4.5 w-4.5" /></span>
            <div>
              <h2 className="font-display text-lg text-ink">Assign complimentary access by person</h2>
              <p className="mt-0.5 text-sm text-ink-soft">Grant a specific person free access — choose the book, the Language Vault, or both. Add their name so you can track who has what.</p>
            </div>
          </div>

          <div className="mt-5">
            <label className="text-xs text-ink-muted">Which offering(s) to give this person</label>
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" onClick={() => setOfferBook((v) => !v)} data-testid="comp-offer-book"
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-display transition ${offerBook ? "bg-ink text-ivory-50" : "bg-ivory-100 text-ink-soft ring-1 ring-ivory-400/70"}`}>
                {offerBook ? <Check className="h-3.5 w-3.5" /> : <BookOpen className="h-3.5 w-3.5" />} The Book
              </button>
              <button type="button" onClick={() => setOfferVault((v) => !v)} data-testid="comp-offer-vault"
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-display transition ${offerVault ? "bg-sage-deep text-ivory-50" : "bg-ivory-100 text-ink-soft ring-1 ring-ivory-400/70"}`}>
                {offerVault ? <Check className="h-3.5 w-3.5" /> : <BookLock className="h-3.5 w-3.5" />} The Language Vault
              </button>
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs text-ink-muted">Recipient name (optional)</label>
              <input value={cName} onChange={(e) => setCName(e.target.value)} data-testid="comp-name" placeholder="e.g. Dr. Jordan Rivera"
                className="mt-1 w-full rounded-lg border border-ivory-400/70 bg-white px-3 py-2.5 text-sm text-ink focus:border-gold focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-ink-muted">Email (optional — links the code to their account)</label>
              <input value={cEmail} onChange={(e) => setCEmail(e.target.value)} data-testid="comp-email" placeholder="partner@example.com" type="email"
                className="mt-1 w-full rounded-lg border border-ivory-400/70 bg-white px-3 py-2.5 text-sm text-ink focus:border-gold focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-ink-muted">Private note (optional)</label>
              <input value={cNote} onChange={(e) => setCNote(e.target.value)} data-testid="comp-note" placeholder="Why they're getting access"
                className="mt-1 w-full rounded-lg border border-ivory-400/70 bg-white px-3 py-2.5 text-sm text-ink focus:border-gold focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-ink-muted">Device cap</label>
              <input value={cCap} onChange={(e) => setCCap(e.target.value)} data-testid="comp-cap" type="number" min="1" max="25"
                className="mt-1 w-full rounded-lg border border-ivory-400/70 bg-white px-3 py-2.5 text-sm text-ink focus:border-gold focus:outline-none" />
            </div>
          </div>

          {grantErr && <p className="mt-3 text-sm text-terracotta" data-testid="comp-error">{grantErr}</p>}

          <button onClick={grant} disabled={granting} data-testid="comp-submit"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-sm text-ivory-50 disabled:opacity-50">
            {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />} Create & assign access
          </button>

          {granted && (
            <div className="mt-5 rounded-xl bg-sage/15 p-4 ring-1 ring-sage/40" data-testid="comp-result">
              <p className="text-xs uppercase tracking-wide text-ink-muted">Ready to share{granted.name ? ` with ${granted.name}` : ""}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="font-display text-xl tracking-[0.14em] text-ink" data-testid="comp-code">{granted.access_code}</span>
                <button onClick={() => copy(granted.access_code, "code")} data-testid="comp-copy-code"
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3 py-1 text-xs text-ink-soft hover:border-gold/50">
                  {copied === "code" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied === "code" ? "Copied" : "Copy code"}
                </button>
                <button onClick={() => copy(compLinkFor(granted), "complink")} data-testid="comp-copy-link"
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3 py-1 text-xs text-ink-soft hover:border-gold/50">
                  {copied === "complink" ? <Check className="h-3.5 w-3.5" /> : <LinkIcon className="h-3.5 w-3.5" />} {copied === "complink" ? "Copied" : "Copy direct link"}
                </button>
              </div>
              <p className="mt-2 break-all text-xs text-ink-soft" data-testid="comp-link-preview">{compLinkFor(granted)}</p>
              <p className="mt-1 text-xs text-ink-soft">
                Grants: {(granted.tiers || []).includes("book_owner") ? "the book" : ""}
                {(granted.tiers || []).includes("book_owner") && (granted.tiers || []).includes("vault_member") ? " + " : ""}
                {(granted.tiers || []).includes("vault_member") ? "the Language Vault" : ""}
                {" "}· up to {granted.device_cap} devices.
              </p>
            </div>
          )}
        </div>

        <div className="mt-6">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by code or email…"
            data-testid="codes-search"
            className="w-full max-w-sm rounded-full border border-ivory-400/70 bg-ivory-50 px-5 py-2.5 text-sm text-ink placeholder:text-ink-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50"
          />
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5 py-8 sm:px-8">
        {loading && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>}
        {!loading && err && <p className="text-sm text-terracotta" data-testid="codes-error">{err}</p>}
        {!loading && !err && filtered.length === 0 && (
          <div className="rounded-xl bg-ivory-50 p-6 text-center ring-1 ring-ivory-400/50">
            <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-gold/10 text-gold-deep"><KeyRound className="h-5 w-5" /></span>
            <p className="mt-3 text-sm text-ink-muted">No full-book access codes yet.</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl bg-ivory-50 ring-1 ring-ivory-400/50">
            <table className="w-full text-left text-sm" data-testid="codes-table">
              <thead>
                <tr className="border-b border-ivory-400/60 text-xs uppercase tracking-wide text-ink-muted">
                  <th className="px-5 py-3 font-medium">Access code</th>
                  <th className="px-5 py-3 font-medium">Reader</th>
                  <th className="px-5 py-3 font-medium">Sent to</th>
                  <th className="px-5 py-3 font-medium">Opens</th>
                  <th className="px-5 py-3 font-medium">Devices</th>
                  <th className="px-5 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const atCap = (r.device_count || 0) >= (r.device_cap || 0);
                  return (
                    <tr key={r.access_code} className="border-b border-ivory-400/40 last:border-0" data-testid={`code-row-${r.access_code}`}>
                      <td className="px-5 py-3 font-display tracking-[0.12em] text-ink">
                        {r.access_code}
                        {r.comp && <span className="ml-2 inline-flex items-center rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gold-deep" data-testid={`code-comp-${r.access_code}`}>Comp</span>}
                        {r.full_access && <span className="ml-1.5 inline-flex items-center rounded-full bg-sage/25 px-2 py-0.5 text-[10px] uppercase tracking-wide text-ink-soft">Full access</span>}
                      </td>
                      <td className="px-5 py-3 text-ink-soft">{r.comp_name || r.email || "—"}{r.comp_name && r.email ? <span className="block text-xs text-ink-muted">{r.email}</span> : null}</td>
                      <td className="px-5 py-3 text-ink-soft" data-testid={`code-sent-to-${r.access_code}`}>
                        {editingCode === r.access_code ? (
                          <div className="flex items-center gap-1.5">
                            <input value={sentToDraft} onChange={(e) => setSentToDraft(e.target.value)} autoFocus
                              onKeyDown={(e) => { if (e.key === "Enter") saveSentTo(r.access_code); if (e.key === "Escape") { setEditingCode(null); setSentToDraft(""); } }}
                              data-testid={`code-sent-to-input-${r.access_code}`} placeholder="Who received this?"
                              className="w-40 rounded-md border border-ivory-400/70 bg-white px-2 py-1 text-xs text-ink focus:border-gold focus:outline-none" />
                            <button onClick={() => saveSentTo(r.access_code)} disabled={savingSentTo} data-testid={`code-sent-to-save-${r.access_code}`}
                              className="inline-flex items-center rounded-full bg-ink px-2.5 py-1 text-[11px] text-ivory-50 disabled:opacity-50">
                              {savingSentTo ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => startEditSentTo(r)} data-testid={`code-sent-to-edit-${r.access_code}`}
                            className="inline-flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink">
                            {r.sent_to ? <span className="text-ink">{r.sent_to}</span> : <span className="text-ink-muted/70 italic">Add recipient</span>}
                            <Pencil className="h-3 w-3 text-ink-muted" />
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1.5 text-ink-soft" data-testid={`code-opens-${r.access_code}`} title={r.last_opened_at ? `Last opened ${new Date(r.last_opened_at).toLocaleString()}` : "Not opened yet"}>
                          <Eye className="h-3.5 w-3.5 text-ink-muted" />
                          <span className="text-ink">{r.open_count || 0}</span>
                          {r.unique_opens > 0 && <span className="text-xs text-ink-muted">· {r.unique_opens} device{r.unique_opens === 1 ? "" : "s"}</span>}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs ${atCap ? "bg-terracotta/20 text-terracotta" : "bg-sage/25 text-ink-soft"}`}
                          data-testid={`code-devices-${r.access_code}`}
                        >
                          {r.device_count} / {r.device_cap}{atCap ? " · full" : ""}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex flex-wrap items-center justify-end gap-2">
                          {r.email && (
                            justResent === r.access_code ? (
                              <span className="inline-flex items-center gap-1.5 text-xs text-gold-deep"><Check className="h-4 w-4" /> Sent</span>
                            ) : (
                              <button
                                onClick={() => resendAccess(r.access_code)}
                                disabled={resending === r.access_code}
                                data-testid={`code-resend-${r.access_code}`}
                                title="Resend access message"
                                className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-3.5 py-1.5 text-xs text-gold-deep transition hover:bg-gold/10 disabled:opacity-40"
                              >
                                {resending === r.access_code ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                Resend access
                              </button>
                            )
                          )}
                          {justReset === r.access_code ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-gold-deep"><Check className="h-4 w-4" /> Reset</span>
                          ) : (
                            <button
                              onClick={() => resetDevices(r.access_code)}
                              disabled={resetting === r.access_code || (r.device_count || 0) === 0}
                              data-testid={`code-reset-${r.access_code}`}
                              className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3.5 py-1.5 text-xs text-ink-soft transition hover:border-gold/50 hover:text-ink disabled:opacity-40"
                            >
                              {resetting === r.access_code ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                              Reset devices
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
